export type BatteryTone = 'healthy' | 'warning' | 'critical';

export interface BatteryEstimate {
  pct: number;
  label: string;
  tone: BatteryTone;
}

export function batteryTone(pct: number): BatteryTone {
  if (pct < 10) return 'critical';
  if (pct < 30) return 'warning';
  return 'healthy';
}

/**
 * Projects remaining battery life from health % and purchase date using a
 * linear degradation model (wear rate observed so far, extrapolated forward).
 * Falls back to a plain health % when there isn't enough data to project from.
 */
export function estimateBatteryLife(pct: number, purchasedAt?: string): BatteryEstimate {
  const tone = batteryTone(pct);
  const clamped = Math.max(0, Math.min(100, pct));

  if (tone === 'critical') {
    return { pct: clamped, label: 'Time to swap', tone };
  }

  const purchaseMs = purchasedAt ? new Date(purchasedAt).getTime() : NaN;
  const degradedPct = 100 - clamped;
  if (Number.isNaN(purchaseMs) || degradedPct <= 0) {
    return { pct: clamped, label: `${Math.round(clamped)}% battery health`, tone };
  }

  const daysSincePurchase = Math.max(1, (Date.now() - purchaseMs) / 86_400_000);
  const remainingDays = (clamped / degradedPct) * daysSincePurchase;

  if (remainingDays >= 365) {
    const years = remainingDays / 365;
    return { pct: clamped, label: `${years.toFixed(years >= 10 ? 0 : 1)} years left`, tone };
  }
  return { pct: clamped, label: `${Math.round(remainingDays)} days left`, tone };
}
